import { useState, useEffect, useTransition, useRef, useCallback } from 'react';

export const useServerAction = <P extends unknown[], R>(
  action: (...args: P) => Promise<R>,
  onFinished?: (_: R | undefined) => void
): [(...args: P) => Promise<R | undefined>, boolean] => {
  const [isPending, startTransition] = useTransition();
  const [result, setResult] = useState<R>();
  const [finished, setFinished] = useState(false);
  const resolver = useRef<(value?: R | PromiseLike<R>) => void>();

  useEffect(() => {
    if (!finished) return;
    if (onFinished) onFinished(result);
    resolver.current?.(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, finished]);

  const runAction = useCallback(
    async (...args: P): Promise<R | undefined> => {
      startTransition(() => {
        action(...args)
          .then((data) => {
            setResult(data);
            setFinished(true);
          })
          .catch((error) => {
            return error;
          });
      });

      return new Promise((resolve) => {
        resolver.current = resolve;
      });
    },
    [action]
  );

  return [runAction, isPending];
};
